import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Section, FadeIn } from '../components/ui-bits';

// Generic legal page wrapper used by Privacy and Terms
export const LegalPage = ({ eyebrow, title, subtitle, lastUpdated, children, testid, backTo, backLabel }) => {
  const { t } = useLanguage();
  const to = backTo || '/que-hacemos/desarrollo-software';
  const label = backLabel || `${t.common.back} · Leitpalel`;
  return (
    <div data-testid={testid}>
      <div className="pt-36 md:pt-44 pb-12 md:pb-16 px-6 md:px-10 lg:px-12 relative overflow-hidden">
        <div className="absolute inset-0 light-beam pointer-events-none" />
        <div className="max-w-4xl mx-auto relative">
          <FadeIn>
            <Link
              to={to}
              className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-amber-300 transition-colors mb-8"
              data-testid="legal-back-link"
            >
              <ArrowLeft className="w-4 h-4" /> {label}
            </Link>
          </FadeIn>
          <FadeIn delay={0.05}>
            <div className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-amber-300/85">
              <span className="inline-block w-7 h-px bg-amber-300/60" />
              <span>{eyebrow}</span>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <h1 className="mt-6 font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]">
              {title}
            </h1>
          </FadeIn>
          <FadeIn delay={0.18}>
            <p className="mt-6 font-body text-base sm:text-lg text-white/55">
              {subtitle} · <span className="text-white/40">{lastUpdated}</span>
            </p>
          </FadeIn>
        </div>
      </div>

      <Section className="pb-32">
        <div className="max-w-3xl mx-auto">
          <FadeIn>
            <article className="prose-legal text-white/75 font-body text-base leading-relaxed">
              {children}
            </article>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div className="mt-16 pt-10 border-t border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="text-sm text-white/45">
                Inversiones Tair SpA · tair.site
              </div>
              <a
                href="mailto:info@tair.site"
                className="inline-flex items-center gap-2 text-sm text-amber-300 hover:text-amber-200"
                data-testid="legal-support-email"
              >
                <Mail className="w-4 h-4" /> info@tair.site
              </a>
            </div>
          </FadeIn>
        </div>
      </Section>
    </div>
  );
};

// Heading and paragraph helpers with consistent styling
export const H2 = ({ children }) => (
  <h2 className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight mt-12 mb-5">
    {children}
  </h2>
);

export const H3 = ({ children }) => (
  <h3 className="font-display font-semibold text-lg md:text-xl text-white/95 tracking-tight mt-8 mb-3">
    {children}
  </h3>
);

export const P = ({ children, strong }) => (
  <p className={`mb-4 ${strong ? 'text-white/90' : 'text-white/70'}`}>{children}</p>
);

export const UL = ({ items }) => (
  <ul className="space-y-2 mb-6 ml-1">
    {items.map((it, i) => (
      <li key={i} className="flex gap-3 text-white/70">
        <span className="text-amber-300/70 mt-1.5 flex-shrink-0">•</span>
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

export const Callout = ({ title, items }) => (
  <div className="my-8 rounded-2xl border border-amber-300/20 bg-amber-300/[0.04] p-6">
    <div className="text-xs uppercase tracking-[0.18em] text-amber-300/85 mb-4">{title}</div>
    <ul className="space-y-2.5">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3 text-white/80">
          <span className="text-amber-300 mt-1.5 flex-shrink-0">✓</span>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  </div>
);
